import {
  Grid,
  GridNoRecords,
  getSelectedState,
  GridCustomCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent
} from '@progress/kendo-react-grid';
import { useParams } from 'react-router-dom';
import { getter } from '@progress/kendo-react-common';
import { useCallback, useRef, useState } from 'react';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { TableSelectionChangeEvent } from '@progress/kendo-react-data-tools';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';

import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../../components/grid/grid-tool-bar';
import { appStore } from '../../../../stores/app-store';
import { Loader } from '@progress/kendo-react-indicators';
import { useToast } from '../../../../contexts/toast-context';
import { ColumnMenu } from '../../../../components/columnMenu';
import { NoRecords } from '../../../../components/grid/no-records';
import { ActionCell } from '../../../../components/grid/actions-cell';
import { getSelectedIds } from '../../../../utils/report/grid-helper';
import { DefaultGridSettings } from '../../../../constants/grid-settings';
import { DownloadReportTaskAsync } from '../../../../services/event-reports';
import { useGetEventReportGameTab } from '../../../../hooks/services/use-reports';
import { EventReportHeaderResponse } from '../../../../types/responses/event-report-response';

interface TaskProps {
  headerReport: EventReportHeaderResponse;
}

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

export const TasksReportTab = ({ headerReport }: TaskProps) => {
  const { eventId, detailId } = useParams();

  const idGetter = getter('id');

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [selectedState, setSelectedState] = useState<{
    [id: string]: boolean | number[];
  }>({});
  const { onToastOpen } = useToast();

  const { data, isLoading } = useGetEventReportGameTab({
    tab: 'tasks',
    eventId,
    playlistId: detailId,
    params: `${toDataSourceRequestString(dataState)}`
  });

  const renderPercentageColumn = (props: GridCustomCellProps) => {
    return <td {...props.tdProps}>{props.dataItem.percentage}</td>;
  };

  const onActionRow = (props: GridCustomCellProps) => {
    return (
      <ActionCell
        title={''}
        icon={'file_download'}
        themeColor={'primary'}
        className={'fs-2'}
        onClick={() => onDownloadRowToPdf(props.dataItem.id)}
      />
    );
  };

  const onDownloadRowToPdf = async (taskId: number) => {
    try {
      appStore.showLoading();
      await DownloadReportTaskAsync(parseInt(eventId!), headerReport.name, [
        taskId
      ]);

      onToastOpen({
        title: 'Task Report',
        body: <div>Report downloaded.</div>,
        style: 'success'
      });
    } catch (err: any) {
      onToastOpen({
        title: 'Task Report',
        body: <div>Failed to download report.</div>,
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
    }
  };

  const onDownloadSelected = async () => {
    try {
      appStore.showLoading();
      const ids = getSelectedIds(selectedState);
      await DownloadReportTaskAsync(parseInt(eventId!), headerReport.name, ids);

      onToastOpen({
        title: 'Task Report',
        body: <div>Report downloaded.</div>,
        style: 'success'
      });
    } catch (err: any) {
      onToastOpen({
        title: 'Task Report',
        body: <div>Failed to download report.</div>,
        style: 'error'
      });
    } finally {
      appStore.hideLoading();
    }
  };

  const onSelectionChange = useCallback(
    (event: TableSelectionChangeEvent<any>) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState: selectedState,
        dataItemKey: 'id'
      });
      setSelectedState(newSelectedState);
    },
    [selectedState]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement = event.syntheticEvent.currentTarget;
      const checked = checkboxElement.checked;
      const newSelectedState: any = {};

      event.dataItems.forEach((item) => {
        newSelectedState[idGetter(item)] = checked;
      });
      setSelectedState(newSelectedState);
    },
    []
  );

  const gridExportRef = useRef<ExcelExport | null>(null);

  return (
    <div
      className={'pt-2'}
      style={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        width: '100%',
        overflow: 'hidden'
      }}>
      <GridToolBar
        searchPlaceholder={'Search task'}
        columnsToSearch={['name']}
        exportRef={gridExportRef}
        showGridMode={false}
        showCardMode={false}
        showBulkExport={true}
        handleBulkExport={onDownloadSelected}
        isDisablePdfButton={getSelectedIds(selectedState).length === 0}
        {...dataState}
        onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
          setDataState(e.dataState);
        }}
      />
      <ExcelExport
        data={data?.data ?? []}
        ref={gridExportRef}
        fileName={`task.xlsx`}>
        <Grid
          id={'grid-task'}
          style={{
            flexGrow: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden'
          }}
          pageable={DefaultGridSettings.pagerSettings}
          sortable={true}
          dataItemKey={'id'}
          data={data}
          select={selectedState}
          selectable={{
            enabled: true,
            drag: false,
            cell: false,
            mode: 'multiple'
          }}
          onSelectionChange={onSelectionChange}
          onHeaderSelectionChange={onHeaderSelectionChange}
          {...dataState}
          onDataStateChange={(e: GridDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}>
          <Column
            field={'selected'}
            width="50px"
            columnType="checkbox"
            headerSelectionValue={
              (data?.data ?? []).length > 0 &&
              (data?.data ?? []).findIndex(
                (item) => !selectedState[idGetter(item)]
              ) === -1
            }
          />
          <Column field={'name'} title={'Name'} columnMenu={ColumnMenu} />
          <Column
            field={'percentage'}
            title={'Percentage'}
            columnMenu={ColumnMenu}
            cells={{ data: renderPercentageColumn }}
          />
          <Column field={'activity'} title={'Game'} columnMenu={ColumnMenu} />
          <Column title={'Actions'} cells={{ data: onActionRow }} width={50} />

          <GridNoRecords>
            {isLoading ? (
              <div
                className={
                  'd-flex flex-column align-items-center gap-2 text-muted'
                }>
                <Loader type={'pulsing'} />
                <span>Loading data</span>
              </div>
            ) : (
              <NoRecords />
            )}
          </GridNoRecords>
        </Grid>
      </ExcelExport>
    </div>
  );
};
